import * as React from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const VERTICALS = [
  { id: "hospitality",         name: "Hospitality",          tagline: "Restaurants, cafés, bars." },
  { id: "retail",              name: "Retail",               tagline: "Multi-store operators." },
  { id: "salon",               name: "Salon & Grooming",     tagline: "Hair, nails, beauty studios." },
  { id: "automotive_sales",    name: "Automotive sales",     tagline: "Dealerships and used-car lots." },
  { id: "rental",              name: "Rental",               tagline: "Equipment, vehicles, kit hires." },
  { id: "architecture_studio", name: "Architecture / design",tagline: "Client-facing studios." },
  { id: "council_public_sector", name: "Council / public",   tagline: "Public appointments." },
  { id: "security",            name: "Security services",    tagline: "Static guarding, patrols." },
  { id: "project_management",  name: "Project teams",        tagline: "Delivery teams in cycles." },
  { id: "other",               name: "Other / custom",       tagline: "Set your own." },
];

/**
 * Onboarding — authenticated-user landing.
 * Redirects to existing company if any; otherwise renders a vertical
 * picker + name + Create.
 */
export default function Onboarding() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [companies, setCompanies] = React.useState(null);
  const [name, setName] = React.useState("");
  const [vertical, setVertical] = React.useState("hospitality");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/companies");
        setCompanies(data);
        if (data && data.length > 0) nav(`/c/${data[0].id}`, { replace: true });
      } catch { setCompanies([]); }
    })();
  }, [nav]);

  async function create(e) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/companies", { name: name.trim(), vertical });
      toast.success(`Created ${data.company.name}`);
      nav(`/c/${data.company.id}`, { replace: true });
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  if (companies === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="onboarding-loading">
        <div className="h-8 w-8 rounded-full border-2 border-brand-rule border-t-brand-violet animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 md:px-6 py-10" data-testid="onboarding">
      <div className="w-full max-w-[720px]">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-6 w-6 rounded-md bg-brand-violet flex items-center justify-center text-white font-bold text-[13px]">F</div>
          <div className="font-bold tracking-tight text-[14.5px] text-brand-ink">Fable Ops</div>
        </div>
        <div className="micro-label">Welcome, {user?.name?.split(" ")[0]}</div>
        <h1 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">Create your first company</h1>
        <p className="mt-1 text-[12px] text-brand-muted">
          Pick your industry so we can seed sensible defaults — you can change these
          any time in Settings.
        </p>
        <form onSubmit={create} className="mt-6 space-y-5">
          <div>
            <label className="micro-label">Company name</label>
            <input className="field-input mt-1 max-w-[420px]" value={name} onChange={(e)=>setName(e.target.value)}
                   placeholder="e.g. Harbour & Vine Group" autoFocus
                   data-testid="onboarding-name" />
          </div>
          <div>
            <label className="micro-label">Industry</label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {VERTICALS.map((v) => (
                <label key={v.id}
                       className={`p-3 rounded-md border cursor-pointer text-left ${
                         vertical === v.id ? "border-brand-violet bg-brand-violet/5" : "border-brand-rule hover:border-brand-violet"
                       }`}
                       data-testid={`onboarding-vertical-${v.id}`}>
                  <input type="radio" name="vertical" className="hidden" checked={vertical === v.id}
                         onChange={()=>setVertical(v.id)} />
                  <div className="text-[13px] font-semibold text-brand-ink">{v.name}</div>
                  <div className="text-[11.5px] text-brand-muted mt-0.5">{v.tagline}</div>
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="btn-violet" disabled={busy || !name.trim()}
                  data-testid="onboarding-submit">
            {busy ? "…" : "Create company"}
          </button>
        </form>
      </div>
    </div>
  );
}
